import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Link } from "react-router";
import { ASSIGN_PICK_ALL_LIMIT, type StudentSummary } from "@edu/shared";
import { studentCountText } from "../../../lib/plurals.ts";
import { Loading } from "../../../lib/loading.tsx";
import { stepActive, validActive } from "./picker-nav.ts";

/**
 * Выбор учеников для выдачи домашки (5.4 docs/homework-phase2-design.md).
 *
 * С 25.09.2026 (просьба владельца) это поле с выпадающим списком, а не список
 * галочек во всю карточку: выбранные стоят плашками в самом поле, поиск печатается
 * там же, а строки с галочками раскрываются под полем. Список галочек на
 * тридцати учениках занимал экран целиком и отодвигал задания за край, а форма
 * теперь двухколоночная и целится в десктоп. Прежний свёрнутый вид «пришли с
 * карточки ученика» (`ChosenStudent`) снят тем же днём: закрытое поле с одной
 * плашкой говорит ровно то же самое и не требует второго компонента.
 *
 * Правила ниже пережили переезд дословно.
 *
 * ⚠️ Список ПУСТОЙ, ничего не предотмечено, и это решение владельца с
 * названной ценой. Вариант «как в прошлый раз» отвергнут: у репетитора несколько
 * потоков одновременно (пять девятиклассников, три семиклассника, шесть
 * одиннадцатиклассников), и предотмеченный не тот ученик хуже пустого списка,
 * потому что домашка уходит человеку, которому не предназначалась, и отменить
 * это нельзя, он её уже видел. Пустой список стоит нескольких нажатий, ошибка
 * стоит доверия.
 *
 * ⚠️ Отвязанные ПОКАЗАНЫ и не выбираемы. Прятать нельзя: человек ищет глазами
 * имя, и его отсутствие читается как «ученика удалили». Сервер и так ответил бы
 * `409 student_not_linked`, но узнать это до нажатия дешевле. Плашки у
 * отвязанного не бывает: плашки рисуются из `chosen` страницы, а страница
 * отвязанных отфильтровывает сама, и второго фильтра здесь нет намеренно.
 *
 * ⚠️ Выбор строки список НЕ закрывает и поиск не очищает: выбор множественный,
 * и закрытие после каждой галочки превратило бы шесть учеников в шесть
 * раскрытий. Закрывают его Escape, уход фокуса из компонента и нажатие мимо.
 */

const dayFmt = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short" });

/** Одна и та же ссылка на пустой массив: иначе `useMemo` пересчитывает зря. */
const NO_STUDENTS: StudentSummary[] = [];

const STATE_TEXT: Record<string, string> = {
  not_opened: "не открыта",
  in_progress: "в работе",
  done: "сдана",
};

/**
 * Подпись строки: два признака, помогающие не перепутать поток.
 *
 * ⚠️ Первым стоит дата занятия, а не последняя работа, и это правка второго
 * круга ревью: раздача выдаёт всем одно и то же, значит сразу после самого
 * частого использования фичи подпись «последняя работа» у всех участников
 * одинакова, и признак ломается тем чаще, чем активнее продукт используют.
 * Потоки же различаются днями недели, и этот признак раздачей не портится.
 */
function hintOf(s: StudentSummary): string {
  const parts: string[] = [];
  const lastLessonAt = s.lastLessonAt ?? null;
  if (lastLessonAt !== null) parts.push(`занятие ${dayFmt.format(new Date(lastLessonAt))}`);
  const last = s.lastHomework ?? null;
  if (last === null) parts.push("домашек ещё не было");
  else parts.push(`последняя домашка: «${last.title}», ${STATE_TEXT[last.state] ?? last.state}`);
  return parts.join(" · ");
}

/**
 * Порядок: свежие занятия сверху, «занятий не было» ниже по алфавиту.
 *
 * Поток, с которым преподаватель работал на этой неделе, оказывается наверху
 * сам, без групп и тегов, и в отличие от даты работы этот порядок не
 * схлопывается после раздачи.
 */
function byRecency(a: StudentSummary, b: StudentSummary): number {
  const at = a.lastLessonAt ?? null;
  const bt = b.lastLessonAt ?? null;
  if (at !== null && bt !== null) return bt - at;
  if (at !== null) return -1;
  if (bt !== null) return 1;
  return a.displayName.localeCompare(b.displayName, "ru");
}

/** Имена в подтверждении: длинный список человек проглядывает, а не читает. */
function namesLine(names: string[], limit = 10): string {
  if (names.length <= limit) return names.join(", ");
  return `${names.slice(0, limit).join(", ")} и ещё ${names.length - limit}`;
}

export function StudentPicker(props: {
  /** `null` означает «ещё грузится»: пустой список и загрузка это разные слова. */
  students: StudentSummary[] | null;
  /**
   * Выбранные И выбираемые, в том виде, в каком их считает страница. Из них
   * рисуются плашки, галочки и счётчик, поэтому отвязанный, пришедший адресом,
   * не получит ни плашки, ни «Выбрано 1».
   */
  chosen: readonly StudentSummary[];
  /**
   * Строка под полем про ученика из адреса (`?studentId=`), которого в поле
   * нет: он отвязан или его нет в списке вовсе. Без неё человек, пришедший с
   * карточки, увидел бы пустое поле и не понял почему. Текст и условие
   * показа считает страница: пикер не знает про адрес.
   */
  linkNote: { text: string; error: boolean } | null;
  busy: boolean;
  onToggle: (userId: string) => void;
  onPickMany: (userIds: string[]) => void;
  onClear: () => void;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [confirming, setConfirming] = useState<StudentSummary[] | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const headId = useId();
  const listId = useId();
  const optionId = (userId: string): string => `${listId}-${userId}`;

  const query = search.trim().toLocaleLowerCase("ru");
  const all = props.students ?? NO_STUDENTS;
  const shown = useMemo(() => {
    const filtered =
      query === ""
        ? all
        : all.filter((s) => s.displayName.toLocaleLowerCase("ru").includes(query));
    return [...filtered].sort(byRecency);
  }, [all, query]);
  const disabled = useMemo(() => shown.map((s) => s.unlinked), [shown]);
  const activeIdx = validActive(disabled, active);
  const activeRow = activeIdx === -1 ? null : (shown[activeIdx] ?? null);

  const chosenIds = useMemo(() => new Set(props.chosen.map((s) => s.userId)), [props.chosen]);
  const pickable = shown.filter((s) => !s.unlinked);

  /**
   * ⚠️ «Выбрать всех» доступно и без поиска, но не всегда (правка находки 14
   * второго круга). У первого пользователя учеников восемь, поток один, «задать
   * всем» это частое намерение, а искать в списке из восьми нечего. Больше
   * десяти это уже несколько потоков, и однокликовая раздача им всем сразу
   * необратима, поэтому там кнопка живёт только при активном поиске.
   */
  const searching = query !== "";
  const canPickAll = pickable.length > 0 && (all.length <= ASSIGN_PICK_ALL_LIMIT || searching);

  /**
   * Закрытие списка. ⚠️ Набранный поиск Escape оставляет, а уход фокуса и
   * нажатие мимо очищают: Escape это «спрячь список, я ещё тут», и человек
   * часто откроет его снова с тем же запросом, а ушедший из поля к заданиям
   * вернётся уже за другим учеником, и старый запрос спрятал бы от него
   * остальных.
   */
  const close = (clearSearch: boolean): void => {
    setOpen(false);
    setActive(-1);
    setConfirming(null);
    if (clearSearch) setSearch("");
  };

  /*
    Нажатие мимо компонента закрывает список. Слушатель живёт только пока список
    открыт: на странице он единственный, но держать его всё время незачем
  */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent): void => {
      const root = rootRef.current;
      if (root !== null && e.target instanceof Node && root.contains(e.target)) return;
      setOpen(false);
      setActive(-1);
      setConfirming(null);
      setSearch("");
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  // Активная строка, уехавшая стрелкой за край прокрутки, возвращается в поле зрения
  const activeId = activeRow === null ? null : optionId(activeRow.userId);
  useEffect(() => {
    if (activeId === null) return;
    document.getElementById(activeId)?.scrollIntoView({ block: "nearest" });
  }, [activeId]);

  const toggle = (s: StudentSummary): void => {
    if (props.busy || s.unlinked) return;
    props.onToggle(s.userId);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    switch (e.key) {
      case "ArrowDown":
      case "ArrowUp": {
        e.preventDefault();
        const dir = e.key === "ArrowDown" ? 1 : -1;
        setOpen(true);
        setActive((prev) => stepActive(disabled, prev, dir));
        return;
      }
      case "Enter":
        if (open && activeRow !== null) {
          e.preventDefault();
          toggle(activeRow);
        }
        return;
      case "Escape":
        // Сначала снимается открытое подтверждение, потом сам список
        if (confirming !== null) {
          e.preventDefault();
          setConfirming(null);
        } else if (open) {
          e.preventDefault();
          close(false);
        }
        return;
      case "Backspace": {
        const last = props.chosen[props.chosen.length - 1];
        if (search === "" && last !== undefined && !props.busy) {
          e.preventDefault();
          props.onToggle(last.userId);
        }
        return;
      }
      default:
        return;
    }
  };

  const refocus = (): void => inputRef.current?.focus();

  /*
    Открытое подтверждение забирает фокус на «Отметить». ⚠️ Без этого кнопка
    «Выбрать всех», нажатая с клавиатуры, исчезает из-под фокуса (на её месте
    встаёт подтверждение), фокус падает на body, и подтверждение не достать ни
    Tab, ни Enter. Мышью фокус и так остаётся в вводе, и перевод его на кнопку
    ничего не ломает: Enter тогда отмечает, Escape отменяет
  */
  const confirmRef = useRef<HTMLButtonElement>(null);
  const confirmOpen = confirming !== null;
  useEffect(() => {
    if (confirmOpen) confirmRef.current?.focus();
  }, [confirmOpen]);

  return (
    <section className="card">
      <div className="combo__head">
        <h2 id={headId}>Кому</h2>
        {/*
          Тихий вес у «Снять выбор»: кнопка правит ОТБОР, а не выдаёт домашку.
          Обычным весом она соревновалась бы за внимание с «Задать»
        */}
        {props.chosen.length > 0 && (
          <>
            <strong className="batch__count">Выбрано {props.chosen.length}</strong>
            <button
              type="button"
              className="btn btn--sm btn--quiet"
              disabled={props.busy}
              onClick={props.onClear}
            >
              Снять выбор
            </button>
          </>
        )}
      </div>

      {props.students === null && <Loading />}
      {/* Форма выдачи без единого ученика бессмысленна: отправляем туда, где он заводится */}
      {props.students !== null && all.length === 0 && (
        <p className="muted">
          Учеников пока нет.{" "}
          <Link className="textlink" to="/students">
            Заведите ученика и отправьте ему ссылку
          </Link>
          .
        </p>
      )}

      {all.length > 0 && (
        <div
          ref={rootRef}
          className="combo"
          /*
            Фокус, ушедший за пределы компонента (Tab дальше по форме), список
            закрывает. ⚠️ Закрывает именно УХОД из компонента, а не любой Tab:
            кнопки «Выбрать всех» и подтверждения стоят внутри списка, и Tab,
            закрывающий его безусловно, делал бы их недостижимыми с клавиатуры
          */
          onBlur={(e) => {
            const next = e.relatedTarget;
            if (next instanceof Node && e.currentTarget.contains(next)) return;
            /*
              Фокус потерян потому, что его узел размонтирован (кнопка «Выбрать
              всех» уступила место подтверждению), а не потому, что человек ушёл.
              Браузеры, которые шлют на это blur, шлют его с пустым
              `relatedTarget`, и закрытие здесь сорвало бы подтверждение
            */
            if (next === null && e.target instanceof Node && !e.target.isConnected) return;
            close(true);
          }}
        >
          <div
            className="combo__field"
            // Нажатие по полю мимо самого ввода не отнимает у ввода фокус
            onMouseDown={(e) => {
              if (e.target !== inputRef.current) e.preventDefault();
            }}
            onClick={() => {
              setOpen(true);
              refocus();
            }}
          >
            {props.chosen.map((s) => (
              <span key={s.userId} className="combo__tag">
                <span className="combo__tagname">{s.displayName}</span>
                <button
                  type="button"
                  className="btn btn--ico btn--sm btn--quiet"
                  aria-label={`Убрать: ${s.displayName}`}
                  disabled={props.busy}
                  onClick={(e) => {
                    // Убрать плашку не значит раскрыть список
                    e.stopPropagation();
                    props.onToggle(s.userId);
                    // Плашка уходит вместе с кнопкой: фокус остаётся в поле, а не на body
                    refocus();
                  }}
                >
                  ✕
                </button>
              </span>
            ))}
            <input
              ref={inputRef}
              className="combo__input"
              type="text"
              role="combobox"
              autoComplete="off"
              spellCheck={false}
              aria-labelledby={headId}
              aria-autocomplete="list"
              aria-expanded={open}
              aria-controls={listId}
              aria-activedescendant={open && activeId !== null ? activeId : undefined}
              value={search}
              placeholder={props.chosen.length === 0 ? "Выберите учеников" : undefined}
              onFocus={() => setOpen(true)}
              onChange={(e) => {
                setSearch(e.target.value);
                setActive(-1);
                setOpen(true);
              }}
              onKeyDown={onKeyDown}
            />
          </div>

          <div
            className="combo__pop"
            hidden={!open}
            // Строки и кнопки списка фокус у ввода не забирают: стрелки и
            // печать продолжают работать сразу после щелчка мышью
            onMouseDown={(e) => e.preventDefault()}
          >
            {/*
              Перечисление имён в подтверждении обязательно, оно и есть защита:
              ошибка видна ДО нажатия, а не после того, как домашку прочитали
            */}
            {confirming !== null ? (
              <div
                className="picker__confirm"
                onKeyDown={(e) => {
                  if (e.key !== "Escape") return;
                  e.preventDefault();
                  setConfirming(null);
                  refocus();
                }}
              >
                <p>
                  Отметить {studentCountText(confirming.length)}:{" "}
                  {namesLine(confirming.map((s) => s.displayName))}
                </p>
                <div className="acts">
                  <button
                    ref={confirmRef}
                    type="button"
                    className="btn"
                    onClick={() => {
                      props.onPickMany(confirming.map((s) => s.userId));
                      setConfirming(null);
                      refocus();
                    }}
                  >
                    Отметить
                  </button>
                  <button
                    type="button"
                    className="btn btn--quiet"
                    onClick={() => {
                      setConfirming(null);
                      refocus();
                    }}
                  >
                    Отмена
                  </button>
                </div>
              </div>
            ) : (
              canPickAll && (
                <div className="combo__tools">
                  <button
                    type="button"
                    className="btn btn--sm btn--quiet"
                    disabled={props.busy}
                    onClick={() => setConfirming(pickable)}
                  >
                    {searching
                      ? `Выбрать всех найденных (${pickable.length})`
                      : `Выбрать всех (${pickable.length})`}
                  </button>
                </div>
              )
            )}

            {shown.length === 0 && (
              <p className="muted combo__none">По запросу «{search}» никого не нашлось.</p>
            )}

            <ul
              id={listId}
              className="combo__list"
              role="listbox"
              aria-multiselectable="true"
              aria-labelledby={headId}
            >
              {shown.map((s, i) => {
                const picked = chosenIds.has(s.userId);
                return (
                  <li
                    key={s.userId}
                    id={optionId(s.userId)}
                    className="combo__opt"
                    role="option"
                    aria-selected={picked}
                    aria-disabled={s.unlinked || undefined}
                    data-active={i === activeIdx || undefined}
                    onClick={() => toggle(s)}
                  >
                    <span className="combo__check" aria-hidden="true">
                      {picked && (
                        <svg viewBox="0 0 16 16" focusable="false">
                          <path d="M3.5 8.5l3 3 6-7" />
                        </svg>
                      )}
                    </span>
                    <span className="picker__name">{s.displayName}</span>
                    <span className="muted picker__hint">
                      {s.unlinked ? "отвязан: отправьте ему новую ссылку" : hintOf(s)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {props.linkNote !== null && (
        <p className={props.linkNote.error ? "err" : "muted"}>{props.linkNote.text}</p>
      )}
    </section>
  );
}
